/**
 * The Arenas page's request. The arena registry, the venue-string resolver
 * and every count come from `sports_tracker.venues.build_league_map`.
 */
import { authedFetch } from "./api";
import { orThrow } from "./http";

export type ArenaCategory = "favored" | "visited" | "unvisited";

export interface ArenaPoint {
  name: string;
  city: string;
  team: string | null;
  lat: number;
  lon: number;
  /** "nhl" for a league arena; "outdoor", "international", "historical" otherwise. */
  kind: string;
  is_primary: boolean;
  category: ArenaCategory;
  games: number;
  /** "2009-2026", or "-" when never visited. */
  seasons: string;
}

export interface ArenasDto {
  league: string;
  leagues: string[];
  team: string;
  teams: string[];
  visited: number;
  total: number;
  favored_count: number;
  arenas: ArenaPoint[];
  unvisited: { name: string; team: string | null; city: string }[];
  unresolved: { venue_name: string | null; home_team: string | null; games: number }[];
}

export async function fetchArenas(league?: string, team?: string): Promise<ArenasDto> {
  const sp = new URLSearchParams();
  if (league) sp.set("league", league);
  if (team) sp.set("team", team);
  const qs = sp.toString();
  const res = await authedFetch(`/api/arenas${qs ? `?${qs}` : ""}`);
  return (await orThrow(res, "Could not load arenas")) as ArenasDto;
}

/**
 * Marker radius: games capped at 12 before the square root, so TD Garden's
 * 240-odd games do not swallow the Northeast.
 */
export function markerRadius(p: Pick<ArenaPoint, "games">): number {
  return p.games ? 4 + Math.sqrt(Math.min(p.games, 12)) * 2.2 : 4;
}

/** The bonus venues (outdoor, international, former), most-visited first. */
export function bonusVenues(arenas: ArenaPoint[]): ArenaPoint[] {
  return arenas.filter((a) => !a.is_primary).sort((a, b) => b.games - a.games);
}
